import type { Connector } from "wagmi"

import type { WalletChainFamily } from "./types"
import type { FeaturedWallet } from "./types"

type WalletChainId = "ethereum" | "bsc" | "polygon" | "arbitrum" | "optimism" | "base" | "tron"

/** EVM networks offered in the pre-connect chain picker. */
export const connectableEvmChainIds: Exclude<WalletChainId, "tron">[] = [
  "ethereum",
  "bsc",
  "polygon",
  "arbitrum",
  "optimism",
  "base",
]

/** Networks shown in the connect-wallet modal for EVM-capable wallets. */
export const connectModalEvmChainIds = [
  "bsc",
  "ethereum",
] as const satisfies readonly WalletChainId[]

type EthereumProvider = {
  isMetaMask?: boolean
  isBraveWallet?: boolean
  isCoinbaseWallet?: boolean
  isTrust?: boolean
  isTrustWallet?: boolean
  isBinance?: boolean
  isMyTonWallet?: boolean
  isImToken?: boolean
  isGemini?: boolean
  isOkxWallet?: boolean
  isBybit?: boolean
  isBitKeep?: boolean
  isRainbow?: boolean
  isBestWallet?: boolean
  isPhantom?: boolean
  providers?: EthereumProvider[]
}

type WalletWindow = Window & {
  ethereum?: EthereumProvider
  tronLink?: unknown
  tronWeb?: unknown
  okxwallet?: unknown
  coinbaseWalletExtension?: unknown
  trustwallet?: unknown
  BinanceChain?: unknown
  bybitWallet?: unknown
  bitkeep?: unknown
  bitget?: unknown
  rainbow?: unknown
  geminiWallet?: unknown
  bestWallet?: unknown
  phantom?: { ethereum?: unknown }
  imToken?: { ethereum?: unknown }
}

const MOBILE_USER_AGENT = /android|iphone|ipad|ipod|harmony/i
const WALLET_BROWSER_USER_AGENT =
  /metamask|trust(?:wallet)?|mytonwallet|mywallet|imtoken|ok(?:app|x)|binance|bybit|bit(?:keep|get)|tronlink|coinbasewallet|phantom/i

function getWalletWindow(): WalletWindow | undefined {
  if (typeof window === "undefined") return undefined
  return window as WalletWindow
}

function listEthereumProviders(): EthereumProvider[] {
  const ethereum = getWalletWindow()?.ethereum
  if (!ethereum) return []
  if (Array.isArray(ethereum.providers) && ethereum.providers.length > 0) {
    return ethereum.providers
  }
  return [ethereum]
}

function connectorMatchesWallet(
  wallet: FeaturedWallet,
  connector: Connector,
): boolean {
  const connectorName = connector.name.toLowerCase()
  return wallet.match.some((term) => connectorName.includes(term.toLowerCase()))
}

function providerMatchesWallet(
  walletId: string,
  provider: EthereumProvider,
): boolean {
  switch (walletId) {
    case "metamask":
      return Boolean(provider.isMetaMask && !provider.isBraveWallet)
    case "binance":
      return Boolean(provider.isBinance)
    case "okx":
      return Boolean(provider.isOkxWallet)
    case "coinbase":
      return Boolean(provider.isCoinbaseWallet)
    case "trust":
      return Boolean(provider.isTrust || provider.isTrustWallet)
    case "bybit":
      return Boolean(provider.isBybit)
    case "bitget":
      return Boolean(provider.isBitKeep)
    case "mywallet":
      return Boolean(provider.isMyTonWallet)
    case "imtoken":
      return Boolean(provider.isImToken)
    case "gemini":
      return Boolean(provider.isGemini)
    case "best-wallet":
      return Boolean(provider.isBestWallet)
    case "rainbow":
      return Boolean(provider.isRainbow)
    case "phantom":
      return Boolean(provider.isPhantom)
    default:
      return false
  }
}

function hasGlobalWalletObject(walletId: string): boolean {
  const w = getWalletWindow()
  if (!w) return false

  switch (walletId) {
    case "binance":
      return Boolean(w.BinanceChain)
    case "okx":
      return Boolean(w.okxwallet)
    case "coinbase":
      return Boolean(w.coinbaseWalletExtension)
    case "trust":
      return Boolean(w.trustwallet)
    case "bybit":
      return Boolean(w.bybitWallet)
    case "bitget":
      return Boolean(w.bitkeep || w.bitget)
    case "imtoken":
      return Boolean(w.imToken?.ethereum)
    case "gemini":
      return Boolean(w.geminiWallet)
    case "best-wallet":
      return Boolean(w.bestWallet)
    case "rainbow":
      return Boolean(w.rainbow)
    case "phantom":
      return Boolean(w.phantom?.ethereum)
    default:
      return false
  }
}

export function isMobileBrowser(): boolean {
  if (typeof navigator === "undefined") return false
  return MOBILE_USER_AGENT.test(navigator.userAgent)
}

/**
 * True when the page is running inside a wallet's DApp browser.
 * Provider injection is authoritative; user-agent markers cover wallets that
 * delay injection until the page requests an account.
 */
export function isWalletBrowserEnvironment(): boolean {
  const w = getWalletWindow()
  if (!w || typeof navigator === "undefined") return false

  const hasInjectedProvider = Boolean(
    w.ethereum ||
      w.tronLink ||
      w.tronWeb ||
      w.okxwallet ||
      w.coinbaseWalletExtension ||
      w.trustwallet ||
      w.BinanceChain ||
      w.bybitWallet ||
      w.bitkeep ||
      w.bitget ||
      w.geminiWallet ||
      w.bestWallet ||
      w.phantom ||
      w.imToken,
  )

  return (
    hasInjectedProvider || WALLET_BROWSER_USER_AGENT.test(navigator.userAgent)
  )
}

/** Ordinary mobile browsers must open the selected wallet's DApp browser. */
export function shouldRedirectToWalletBrowser(): boolean {
  return isMobileBrowser() && !isWalletBrowserEnvironment()
}

function hasWalletBrandInstalled(walletId: string): boolean {
  return (
    listEthereumProviders().some((provider) =>
      providerMatchesWallet(walletId, provider),
    ) || hasGlobalWalletObject(walletId)
  )
}

/**
 * Dual-chain wallets expose TRON via tronLink/tronWeb in their DApp browser,
 * or via the same app over deep link when only the EVM provider is visible.
 */
function hasDualChainTron(walletId: string): boolean {
  const w = getWalletWindow()
  if (!w) return false
  if (w.tronLink || w.tronWeb) return true
  return hasWalletBrandInstalled(walletId)
}

function hasTronProvider(provider?: FeaturedWallet["tronProvider"]): boolean {
  const w = getWalletWindow()
  if (!w) return false

  switch (provider) {
    case "metamask":
      return hasWalletBrandInstalled("metamask")
    case "mywallet":
      return hasDualChainTron("mywallet")
    case "trust":
      return hasDualChainTron("trust")
    case "imtoken":
      return Boolean(w.tronWeb || w.tronLink || w.imToken)
    case "okx":
      return hasDualChainTron("okx")
    case "binance":
      return hasDualChainTron("binance")
    case "bitget":
      return hasDualChainTron("bitget")
    case "bybit":
      return hasDualChainTron("bybit")
    case "tronlink":
    default:
      return Boolean(w.tronLink || w.tronWeb)
  }
}

/** Whether a featured wallet can connect on TRON (native tron chain or dual-chain EVM wallet). */
export function walletSupportsTron(wallet: FeaturedWallet): boolean {
  return wallet.chain === "tron" || Boolean(wallet.tronProvider)
}

/**
 * Networks the user may pick for this wallet before connecting.
 * TRON-native wallets → TRON only; dual-chain → TRON + BNB + Ethereum; EVM-only → BNB + Ethereum.
 */
export function getSupportedConnectChains(
  wallet: FeaturedWallet,
): WalletChainId[] {
  if (wallet.chain === "tron") return ["tron"]
  if (walletSupportsTron(wallet)) {
    return ["tron", ...connectModalEvmChainIds]
  }
  return [...connectModalEvmChainIds]
}

/** Pick EVM vs TRON connect path from the wallet definition and user-selected network. */
export function resolveConnectChain(
  wallet: FeaturedWallet,
  activeChain: WalletChainId,
): WalletChainFamily {
  if (wallet.chain === "tron") return "tron"
  if (!wallet.tronProvider) return "evm"
  // Honor the chain the user picked in the connect modal.
  return activeChain === "tron" ? "tron" : "evm"
}

/** True only when this specific wallet extension / app is available — not generic injected. */
export function isWalletInstalled(
  wallet: FeaturedWallet,
  connectors: readonly Connector[],
  options?: { activeChain?: WalletChainId },
): boolean {
  const connectChain = resolveConnectChain(
    wallet,
    options?.activeChain ?? "ethereum",
  )

  if (connectChain === "tron") {
    return hasTronProvider(wallet.tronProvider ?? "tronlink")
  }

  const evmInstalled =
    connectors.some((connector) => connectorMatchesWallet(wallet, connector)) ||
    listEthereumProviders().some((provider) =>
      providerMatchesWallet(wallet.id, provider),
    ) ||
    hasGlobalWalletObject(wallet.id)

  if (evmInstalled) return true

  if (wallet.tronProvider && hasTronProvider(wallet.tronProvider)) {
    return true
  }

  return false
}

/** Resolve wagmi connector for connect — prefers name match, never generic injected. */
export function resolveEvmConnectorForWallet(
  wallet: FeaturedWallet,
  connectors: readonly Connector[],
): Connector | undefined {
  const namedConnector = connectors.find((connector) =>
    connectorMatchesWallet(wallet, connector),
  )
  if (namedConnector) return namedConnector

  const walletProviderDetected =
    listEthereumProviders().some((provider) =>
      providerMatchesWallet(wallet.id, provider),
    ) || hasGlobalWalletObject(wallet.id)

  const injectedConnector = walletProviderDetected
    ? connectors.find((connector) => connector.id === "injected")
    : undefined

  return injectedConnector
}
